import {
  getAiUsageMockState,
  refreshAiUsageMockState,
} from '../mock-data/ai-usage.mock';
import { createMockResponse } from '../mock.interceptor';

import type { AiUsageApiResponse } from '../../features/ai-usage/ai-usage.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * /ai-usage* へのリクエストをハンドリング（Issue #475 Phase 1）
 * path はテナントプレフィックス除去済みの短縮パス（例: /ai-usage, /ai-usage/refresh）
 */
export function handleAiUsageRequest(
  method: string,
  path: string,
  tenantId: string,
): Observable<HttpResponse<unknown>> | null {
  // GET /ai-usage - スナップショット参照（AI サーバーを叩かない）
  if (method === 'GET' && path === '/ai-usage') {
    return handleGetAiUsage(tenantId);
  }

  // POST /ai-usage/refresh - AI サーバーへ同期照会してスナップショットを更新
  if (method === 'POST' && path === '/ai-usage/refresh') {
    return handleRefreshAiUsage(tenantId);
  }

  return null;
}

/**
 * GET /api/v0/tenants/:tenantId/ai-usage - AI 利用量スナップショット参照
 *
 * 保存済みスナップショットのみを返し、AI サーバーへは照会しない。
 * そのため stale は常に true（= 今回の応答は同期照会で得た最新値ではない）。
 * スナップショット未保存の場合は usages 空・fetchedAt null で 200 を返す。
 */
function handleGetAiUsage(
  tenantId: string,
): Observable<HttpResponse<AiUsageApiResponse>> {
  return createMockResponse<AiUsageApiResponse>(
    buildAiUsageResponse(tenantId, false),
  );
}

/**
 * POST /api/v0/tenants/:tenantId/ai-usage/refresh - AI 利用量の同期照会 + スナップショット更新
 *
 * 照会に成功した場合は最新値を stale: false で返す。
 * API キー未登録・通信エラー・スナップショット未保存の場合もエラーにはせず、
 * 保存値（無ければ空）を stale: true で 200 返却する。
 */
function handleRefreshAiUsage(
  tenantId: string,
): Observable<HttpResponse<AiUsageApiResponse>> {
  const fetched = refreshAiUsageMockState(tenantId);
  return createMockResponse<AiUsageApiResponse>(
    buildAiUsageResponse(tenantId, fetched),
  );
}

/**
 * 保存済みスナップショットからレスポンスを組み立てる
 * @param tenantId テナント ID
 * @param fetchedFromAiServer 今回のリクエストで AI サーバーから取得できたか（stale の反転）
 */
function buildAiUsageResponse(
  tenantId: string,
  fetchedFromAiServer: boolean,
): AiUsageApiResponse {
  const state = getAiUsageMockState(tenantId);

  // スナップショット未保存
  if (!state) {
    return { usages: [], fetchedAt: null, stale: true };
  }

  return {
    // Mock 状態を呼び出し側から書き換えられないようコピーして返す
    usages: state.usages.map((usage) => ({ ...usage })),
    fetchedAt: state.fetchedAt,
    stale: !fetchedFromAiServer,
  };
}
